<template>
  <Teleport to="body">
    <Transition name="review-modal">
      <div
        v-if="isOpen"
        class="review-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-modal-title"
        @click.self="close"
      >
        <div class="review-modal__backdrop" aria-hidden="true" />
        <div class="review-modal__box">
          <button type="button" class="review-modal__close" aria-label="Закрыть" @click="close">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <h2 id="review-modal-title" class="review-modal__title">Написать отзыв</h2>
          <form class="review-modal__form" @submit.prevent="submit">
            <FormInput
              v-model="form.name"
              name="reviewName"
              label="Ваше имя"
              placeholder="Как к вам обращаться?"
              :required="true"
            />
            <FormInput
              v-model="form.reviewText"
              name="reviewText"
              label="Текст отзыва"
              placeholder="Опишите ваш опыт"
              :required="true"
              multiline
              :rows="5"
            />
            <div class="review-modal__photo">
              <label class="review-modal__photo-label">Фото к отзыву (необязательно)</label>
              <input
                ref="photoInput"
                type="file"
                name="photo"
                class="review-modal__photo-input"
                accept="image/*,.heic,.heif,.webp,.png,.jpg,.jpeg"
                @change="onPhotoChange"
              />
              <button type="button" class="review-modal__photo-btn" @click="photoInput?.click()">
                Выберите файл
              </button>
              <p class="review-modal__photo-hint">WebP, PNG, JPG или JPEG. Не более 8 МБ.</p>
              <p v-if="photoError" class="review-modal__photo-error">{{ photoError }}</p>
              <div v-if="form.photoFile" class="review-modal__photo-preview">
                <span class="review-modal__photo-name">{{ form.photoFile.name }}</span>
                <button type="button" class="review-modal__photo-remove" aria-label="Удалить фото" @click="clearPhoto">×</button>
              </div>
            </div>
            <div class="review-modal__actions">
              <button type="button" class="review-modal__btn review-modal__btn_secondary" @click="close">Отмена</button>
              <button type="submit" class="review-modal__btn review-modal__btn_submit" :disabled="sending">
                {{ sending ? 'Отправка…' : 'Отправить' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { getApiUrl } from '@/config/api'
import FormInput from '@/components/FormInput.vue'

const MAX_PHOTO_SIZE = 8 * 1024 * 1024
const ACCEPT_PHOTO = '.webp,.png,.jpg,.jpeg'
const defaultForm = () => ({ name: '', reviewText: '', photoFile: null })

export default {
  name: 'ReviewModal',
  components: { FormInput },
  setup() {
    const store = useStore()
    const isOpen = computed(() => store.state.reviewModalOpen)
    const sending = ref(false)
    const photoError = ref('')
    const photoInput = ref(null)
    const form = reactive(defaultForm())

    function close() {
      store.commit('setReviewModalOpen', false)
    }

    function onPhotoChange(e) {
      photoError.value = ''
      const file = e.target.files && e.target.files[0]
      if (!file) {
        form.photoFile = null
        return
      }
      const extOk = /\.(webp|png|jpe?g|heic|heif)$/i.test(file.name || '')
      const mimeOk = ['image/webp', 'image/png', 'image/jpeg', 'image/jpg', 'image/x-png', 'image/heic', 'image/heif'].includes(file.type || '')
      // iOS Photos often sends empty type; allow by extension or when no extension (backend will accept)
      const emptyTypeFromIOS = (!file.type || file.type === 'application/octet-stream') && (extOk || !file.name || !/\.\w+$/.test(file.name))
      const allowed = extOk || mimeOk || emptyTypeFromIOS
      if (!allowed) {
        photoError.value = 'Только изображения: WebP, PNG, JPG, JPEG.'
        form.photoFile = null
        e.target.value = ''
        return
      }
      if (file.size > MAX_PHOTO_SIZE) {
        photoError.value = 'Файл не более 8 МБ.'
        form.photoFile = null
        e.target.value = ''
        return
      }
      form.photoFile = file
    }

    function clearPhoto() {
      form.photoFile = null
      photoError.value = ''
      if (photoInput.value) photoInput.value.value = ''
    }

    async function submit() {
      if (!form.name.trim() || !form.reviewText.trim()) return
      if (photoError.value) return
      sending.value = true
      try {
        const fd = new FormData()
        fd.append('name', form.name.trim())
        fd.append('reviewText', form.reviewText.trim())
        if (form.photoFile) fd.append('photo', form.photoFile)
        const res = await fetch(getApiUrl('/api/reviews'), {
          method: 'POST',
          body: fd
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          throw new Error(data.error || `HTTP ${res.status}`)
        }
        close()
        store.commit('setToast', 'Спасибо за ваш отзыв!')
        store.dispatch('reviews/fetchReviews')
        clearPhoto()
        Object.assign(form, defaultForm())
      } catch (e) {
        console.error('Review submit error', e)
        store.commit('setToast', e.message || 'Ошибка отправки. Попробуйте позже.')
      } finally {
        sending.value = false
      }
    }

    watch(isOpen, (open) => {
      if (open) {
        clearPhoto()
        Object.assign(form, defaultForm())
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        document.body.style.paddingRight = scrollbarWidth ? `${scrollbarWidth}px` : ''
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
      }
    })

    return { isOpen, form, sending, photoError, photoInput, close, submit, onPhotoChange, clearPhoto }
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/_variables.scss";

$dur: 0.3s;
$ease: cubic-bezier(0.22, 1, 0.36, 1);

.review-modal {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: 'Arimo', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.review-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
}

.review-modal__box {
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  background: $color-primary-inverse;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.25);
  padding: 32px 28px 28px;
  box-sizing: border-box;
}

.review-modal__close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 50%;
  color: $color-text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: $color-text;
  }
  svg { width: 20px; height: 20px; }
}

.review-modal__title {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 24px;
  padding-right: 48px;
  color: $color-text;
}

.review-modal__form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.review-modal__photo {
  margin-top: 4px;
}

.review-modal__photo-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: $color-text;
  margin-bottom: 8px;
}

.review-modal__photo-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
}

.review-modal__photo-btn {
  display: inline-block;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  color: $color-primary-inverse;
  background: $color-accent-teal;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  transition: background 0.2s, transform 0.15s;
  &:hover {
    background: darken($color-accent-teal, 6%);
    transform: scale(1.02);
  }
}

.review-modal__photo-hint {
  font-size: 12px;
  color: $color-text-muted;
  margin: 0 0 8px;
}

.review-modal__photo-error {
  font-size: 13px;
  color: #c53030;
  margin: 0 0 8px;
}

.review-modal__photo-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
}

.review-modal__photo-name {
  font-size: 13px;
  color: $color-text-muted;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.review-modal__photo-remove {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  font-size: 18px;
  line-height: 1;
  color: $color-text-muted;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: rgba(0, 0, 0, 0.08);
    color: $color-text;
  }
}

.review-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid $color-border;
}

.review-modal__btn {
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s, opacity 0.2s;

  &_secondary {
    background: rgba(0, 0, 0, 0.06);
    color: $color-text;
    &:hover { background: rgba(0, 0, 0, 0.1); }
  }

  &_submit {
    background: $color-accent-teal;
    color: $color-primary-inverse;
    &:hover:not(:disabled) { background: darken($color-accent-teal, 6%); }
    &:disabled { opacity: 0.7; cursor: not-allowed; }
  }
}

.review-modal-enter-active,
.review-modal-leave-active {
  transition: opacity $dur $ease;
  .review-modal__box {
    transition: transform $dur $ease;
  }
}
.review-modal-enter-from,
.review-modal-leave-to {
  opacity: 0;
  .review-modal__box {
    transform: scale(0.96) translateY(12px);
  }
}
</style>
